/**
 * The MotionPace hero chart + film-strip visual, shared between the /motionpace
 * page and the homepage MotionPace section (#545 / landing redesign).
 *
 * WHY SHARED: the chart is ~150 lines of SVG + strip + annotations. Duplicating
 * it would mean two places to regenerate when motionPaceHeroData.js is updated.
 * Extracting it here keeps /motionpace pixel-for-pixel identical while letting the
 * homepage section embed the same visual inside its card.
 *
 * GATING: this module is only ever imported from within the motionPaceHome gated
 * module or from MotionPacePage.js. MotionPacePage itself is only bundled when the
 * /motionpace route is active, and motionPaceHome is only required when
 * REACT_APP_MOTIONPACE_MARKETING === 'true'. So this component never reaches a
 * production bundle unless one of those two gates is open. Its stylesheet rides
 * the same gates, because only this module imports it.
 */

import React from 'react';

// The component's own styling. These rules used to live in MotionPacePage.css,
// which left the homepage card styled only as a side effect of App.js pulling
// that page into the main bundle — see the header of this file for the full
// story. Importing here makes the visual self-contained wherever it mounts.
import './motionChartHero.css';
import {
  ASSETS,
  CHART,
  SPEED_TICKS,
  TIME_TICKS,
  ANNOTATIONS,
  HERO_CELLS,
  STRIP_CELL_COUNT,
  SAMPLE,
} from '../motionPaceHeroData';
import { MAX_SPEED_MAX } from '../../lib/motionPace';

const MAX_SPEED_RANGE_MAX = String(MAX_SPEED_MAX);

/**
 * The source-time ruler under a film strip. Rendered inside the chart hero so
 * the ruler always tracks the strip above it.
 */
export function TimeRuler({ className }) {
  return (
    <div className={className ? `mp-ruler ${className}` : 'mp-ruler'} aria-hidden="true">
      {TIME_TICKS.map((tick) => (
        <span key={tick.label} style={{ '--mp-x': tick.x }}>
          {tick.label}
        </span>
      ))}
    </div>
  );
}

/**
 * One card per sampled output frame, positioned at the moment in SOURCE time it
 * was shot. Decorative — hidden from assistive tech.
 */
export function FilmStrip({ variant, cells, sprite }) {
  return (
    <div
      className={`mp-strip mp-strip-${variant}`}
      style={{
        '--mp-sprite': `url('${sprite}')`,
        '--mp-count': STRIP_CELL_COUNT,
      }}
      aria-hidden="true"
      data-testid={`motionpace-strip-${variant}`}
    >
      {cells.map((x, n) => (
        <i
          className="mp-cell"
          key={`${variant}-${n}`}
          style={{ '--mp-n': n, '--mp-x': x }}
        />
      ))}
    </div>
  );
}

/**
 * The full motion chart hero: panel head, SVG chart, film strip, annotations,
 * ruler, and caption footer.
 *
 * `headingLevel` controls whether the panel title is rendered as a `<b>` tag
 * (default, used on /motionpace inside a `<figure>`) or omitted (the section
 * context already has a heading).
 *
 * `showFooter` — when false, the "Every card is a frame…" paragraph is omitted
 * (the landing section has its own "See how MotionPace works →" link below the
 * card instead).
 */
export default function MotionChartHero({ showFooter = true }) {
  return (
    <>
      <div className="mp-panel-head">
        <b>Deck build · {SAMPLE.captureDays} days on a fixed camera</b>
        <span className="mp-panel-meta">
          {SAMPLE.sourceLabel} of source → {SAMPLE.outLabel} adaptively paced · nothing cut
          out · average <em>{SAMPLE.speedMeanLabel}×</em>, peak{' '}
          <em>{SAMPLE.speedMaxLabel}×</em>
        </span>
        <span className="mp-panel-spacer" />
        <span className="mp-legend">
          <span>
            <i className="mp-sw-motion" />
            Motion
          </span>
          <span>
            <i className="mp-sw-speed" />
            Playback speed
          </span>
          <span>
            <i className="mp-sw-max" />
            Max speed
          </span>
        </span>
      </div>

      <div className="mp-plot">
        <div className="mp-chart-wrap">
          <svg
            className="mp-chart"
            viewBox={`0 0 ${CHART.width} ${CHART.height}`}
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="mpCurveFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#00b3a6" stopOpacity=".26" />
                <stop offset="1" stopColor="#00b3a6" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* speed gridlines */}
            <path d={CHART.gridD} stroke="rgba(255,255,255,0.05)" strokeWidth="1" fill="none" />
            {/* per-frame motion, bucketed (production motionAlgorithm v2 values) */}
            <path d={CHART.curveFillD} fill="url(#mpCurveFill)" stroke="none" />
            <path
              d={CHART.barsD}
              stroke="#5a6572"
              strokeOpacity=".72"
              strokeWidth={CHART.barWidth}
              fill="none"
            />
            {/* the ceiling THIS render's Max speed slider was set to */}
            <line
              x1="0"
              y1={CHART.ceilingY}
              x2={CHART.width}
              y2={CHART.ceilingY}
              stroke="rgba(255,255,255,.42)"
              strokeWidth="1"
              strokeDasharray="4 3"
            />
            <text
              x="6"
              y={CHART.ceilingY}
              dy="-5"
              fontSize="10.5"
              fill="rgba(255,255,255,.55)"
              fontFamily="inherit"
            >
              max speed {SAMPLE.ceiling}× — this render&apos;s ceiling (the slider goes to{' '}
              {MAX_SPEED_RANGE_MAX}×)
            </text>
            {/* guides at the two annotated moments */}
            <path
              d={`M${ANNOTATIONS.slow.px} 0V${CHART.baseline}M${ANNOTATIONS.fast.px} 0V${CHART.baseline}`}
              stroke="rgba(255,255,255,.16)"
              strokeWidth="1"
              strokeDasharray="2 4"
            />
            {/* the speed curve — a wide, faint pass under a crisp one */}
            <path
              d={CHART.curveD}
              fill="none"
              stroke="#00b3a6"
              strokeOpacity=".18"
              strokeWidth="8"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <path
              d={CHART.curveD}
              fill="none"
              stroke="#00d4c5"
              strokeWidth="2.3"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <circle
              cx={ANNOTATIONS.slow.px}
              cy={ANNOTATIONS.slow.y}
              r="3.6"
              fill="#00d4c5"
              stroke="#0b0d11"
              strokeWidth="1.6"
            />
            <circle
              cx={ANNOTATIONS.fast.px}
              cy={ANNOTATIONS.fast.y}
              r="3.6"
              fill="#00d4c5"
              stroke="#0b0d11"
              strokeWidth="1.6"
            />
            <line
              x1="0"
              y1={CHART.baseline}
              x2={CHART.width}
              y2={CHART.baseline}
              stroke="rgba(255,255,255,.12)"
              strokeWidth="1"
            />
          </svg>
          <div className="mp-axis" aria-hidden="true">
            {SPEED_TICKS.map((tick) => (
              <span key={tick.speed} style={{ top: `${tick.topPct}%` }}>
                {tick.speed}×
              </span>
            ))}
          </div>
        </div>

        <FilmStrip variant="hero" cells={HERO_CELLS} sprite={ASSETS.stripHero} />

        <div className="mp-anno-row">
          <span className="mp-anno" style={{ left: `${ANNOTATIONS.slow.x}%` }}>
            <b>{ANNOTATIONS.slow.speedLabel}×</b> · crew framing the stairs — frames pile up
          </span>
          <span
            className="mp-anno mp-anno-fast"
            style={{ left: `${ANNOTATIONS.fast.x}%` }}
          >
            <b>{ANNOTATIONS.fast.speedLabel}×</b> · empty site — frames spread apart
          </span>
        </div>
        <TimeRuler />
      </div>

      {showFooter && (
        <figcaption className="mp-panel-foot">
          <b>
            Every card is a frame that made the final cut, dropped at the minute it was shot.
          </b>{' '}
          Where the crew is working they pile up — that stretch plays at 1×. Where the site is
          empty they spread apart — up to {SAMPLE.speedMaxLabel}× in this render. Same{' '}
          {SAMPLE.outFramesLabel} frames either way.{' '}
          <span className="mp-src">
            Real render: {SAMPLE.name} benchmark, motion engine v2.
          </span>
        </figcaption>
      )}
    </>
  );
}
